import { useRef, useState } from 'react';
import { usePopper } from 'react-popper';

import { TooltipView } from './components/TooltipView';
import { Arrow, FakeButton, Root } from './style';

import type { FC, PropsWithChildren } from 'react';
import type { Placement } from '@popperjs/core';

export type TooltipProps = PropsWithChildren<{
  id?: string;
  text?: string;
  placement?: Placement;
  variant?: 'primary' | 'outline';
  arrowCornerPadding?: number;
}>;

export const Tooltip: FC<TooltipProps> = ({
  id,
  children,
  text,
  placement = 'top-end',
  variant = 'primary',
  arrowCornerPadding = 0,
}) => {
  const [visible, setVisible] = useState(false);
  const skipTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [skipClose, setSkipClose] = useState(false);

  const tooltipRef = useRef<HTMLButtonElement>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

  const popperConfig = {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 18] } },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  };
  const { styles, attributes } = usePopper(
    text && visible ? tooltipRef.current : null,
    popperElement,
    popperConfig,
  );

  const showTooltip = () => {
    clearTimeout(skipTimerRef.current);
    setVisible(true);
  };

  const hideTooltipWithTimeout = () => {
    if (skipClose) {
      return;
    }

    clearTimeout(skipTimerRef.current);
    skipTimerRef.current = setTimeout(() => setVisible(false), 100);
  };

  const handleClick = () => {
    visible ? hideTooltipWithTimeout() : showTooltip();
  };

  const handleBlur = () => {
    hideTooltipWithTimeout();
  };

  const handleCloseClick = () => {
    setVisible(false);
  };

  const handleMouseEnter = () => {
    setSkipClose(true);
  };

  const handleMouseLeave = () => {
    setSkipClose(false);
    tooltipRef.current?.focus();
  };

  return (
    <>
      <FakeButton
        type="button"
        aria-expanded={visible}
        aria-haspopup="true"
        aria-label="Show more"
        ref={tooltipRef}
        onClick={handleClick}
        onBlur={handleBlur}
        onKeyUp={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.key === 'Escape') {
            hideTooltipWithTimeout();
          }
        }}
      >
        {children}
      </FakeButton>

      {Boolean(text) && (
        <Root
          id={id}
          role="tooltip"
          tabIndex={0}
          ref={setPopperElement}
          visible={visible}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={styles.popper}
          arrowCornerPadding={arrowCornerPadding}
          {...attributes.popper}
        >
          <TooltipView variant={variant} onCloseClick={handleCloseClick}>
            {text}
          </TooltipView>
          <Arrow ref={setArrowElement} variant={variant} style={styles.arrow} />
        </Root>
      )}
    </>
  );
};
